import { useEffect, useRef } from 'react';

import { randomUUID } from 'expo-crypto';
import { useDonations } from 'features/cart/hooks/use-donations';
import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { commonStyles } from 'features/cart/styles';
import { ICartEntry, MinimalCartEntryType } from 'features/cart/types';
import { StyleSheet, View } from 'react-native';
import { Typography } from 'ucl';

import { useFeatureDonation } from 'hooks/use-feature-donation';
import { StyledToggleButtonGroup } from 'pages/cart/donation-at-checkout/styled';
import { t } from 'state/intl/translations';
import { getServiceModeFromLS } from 'state/service-mode';
import { useUIContext } from 'state/ui';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { getDesignToken, primitive } from 'styles/constants';
import { emptySanityImage } from 'utils/sanity';

import { logCartAddToCart, logCartDonationViewed } from '../../utils/cart-analytics';
import { CartSectionHeader } from '../cart-section-header';

export const DonationsSection = () => {
  const { featureDonation } = useFeatureDonation();
  const { donations, loading } = useDonations();
  const { indexedMenu } = useUnifiedMenuContext();
  const { formatCurrencyForLocale } = useUIContext();
  const { cartEntries, addItemToMinimalCart, removeItemFromMinimalCart } = useMinimalCart();

  const selectDonationAmountText = t('selectDonationAmount');
  const selectedDonation: ICartEntry | undefined = cartEntries.find(
    entry => entry.type === MinimalCartEntryType.donation
  );
  const hasLoggedDonationViewRef = useRef(false);

  const donationOptions = donations.map(donation => ({
    label: formatCurrencyForLocale(donation.price),
    value: donation.id,
    isDisabled: false,
  }));

  const handleChange = (value?: string | null) => {
    if (!value) {
      return;
    }
    if (selectedDonation?.id === value) {
      removeItemFromMinimalCart(selectedDonation);
      return;
    }
    const donation = donations.find(donation => donation.id === value);
    if (!donation) {
      return;
    }
    if (selectedDonation && donation.id !== selectedDonation.id) {
      removeItemFromMinimalCart(selectedDonation);
    }
    const newDonationEntry = {
      id: value,
      cartId: randomUUID(),
      quantity: 1,
      name: donation.name,
      price: {
        amount: donation.price,
        currency: 'USD',
      },
      image: emptySanityImage(),
      url: '',
      type: MinimalCartEntryType.donation,
      children: [],
    };

    const serviceMode = getServiceModeFromLS();
    if (serviceMode) {
      logCartAddToCart({
        cartEntry: newDonationEntry,
        previousCartEntries: cartEntries,
        serviceMode,
      });
    }

    addItemToMinimalCart(newDonationEntry);
  };

  useEffect(() => {
    if (!donations.length || hasLoggedDonationViewRef.current) {
      return;
    }

    hasLoggedDonationViewRef.current = true;
    logCartDonationViewed();
  }, [donations.length]);

  // Match legacy DonationContainer: do not render an empty card when there is nothing to donate
  // (e.g. flag on but no priced items / market without donation SKUs).
  if (loading) {
    return null;
  }
  if (!featureDonation?.donationItemList?.length) {
    return null;
  }
  if (!indexedMenu.isLoaded()) {
    return null;
  }
  if (donations.length === 0) {
    return null;
  }

  return (
    <View style={styles.container}>
      <CartSectionHeader
        title={featureDonation?.header?.locale ?? ''}
        testID="donations-section-heading-label"
      />
      <Typography style={commonStyles.cartEntryBody}>
        {featureDonation?.subtitle?.locale ?? ''}
      </Typography>
      <StyledToggleButtonGroup
        options={donationOptions}
        onChangeValue={handleChange}
        value={selectedDonation?.id}
        accessibilityHint={selectDonationAmountText}
        accessibilityLabel={selectDonationAmountText}
        isDisabled={loading}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 16,
    backgroundColor: getDesignToken('background-card'),
    borderRadius: primitive.$borderRadius,
    overflow: 'hidden',
  },
});
